/**
 * SoakingDashboard.jsx
 * Project  : CCC4CS — Choice Canning Co. PPC/ColdStore
 * Module   : PPC → Soaking
 * Author   : Saravanan R, IT Dept, Choice Group
 * Created  : 2026-04-01
 *
 * Converted from SoakingDashboard_Final.html → React 18
 * Uses same custom-CSS approach as other PPC dashboards (BravoDashboard, PapaDashboard)
 * Brand theme: linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SoakingDashboard.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost/warehouse-system/api'}/soaking.php`;
const MONTHS   = ['A','B','C','D','E','F','G','H','J','K','L','M'];
const DQ       = 240; // default tub weight

const GRADE_DEFAULTS = {
  '8/12':112,'13/15':113,'16/20':114,'21/25':113,'26/30':115,
  '31/40':114,'41/50':113,'51/60':112,'61/70':111,'71/90':110,'100/150':109,'BROKEN':108
};

// Master data — loaded from API (dropdowns)
const PRODUCTS_FB    = ['REZP','CPTO','CISTSR','CEZP','HLSO','CPND','RTNC','CBKN','COOKED'];
const GRADES_FB      = Object.keys(GRADE_DEFAULTS);
const PACK_STYLES_FB = ['FINAL','FBB','PBB','BULK'];

// Entry stages
const STAGES = { DRAFT:'DRAFT', IN_FILLED:'IN_FILLED', OUT_FILLED:'OUT_FILLED', SUBMITTED:'SUBMITTED' };
const STAGE_LABEL = { DRAFT:'Draft', IN_FILLED:'IN Filled', OUT_FILLED:'OUT Filled', SUBMITTED:'Submitted' };
const STAGE_COLOR = { DRAFT:'#9ca3af', IN_FILLED:'#1565c0', OUT_FILLED:'#7c3aed', SUBMITTED:'#059669' };
const EMPTY_FORM  = {
  lot_sfx:'', packing_line:'VICTOR-1', product:'', grade:'', pack_size:'',
  brand:'', soaking_hours:3, is_cooking:false,
  in_big_wt:'', in_small_wt:'', in_count:'', tub_no:1, tub_weight:DQ, soaking_id:'',
  soak_start_time:'', remarks:'', out_big_wt:'', out_small_wt:'', out_count:'',
  expected_pickup:'', dispatch_date:'', dispatch_time:'', unloading_date:'', unloading_time:'',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2,'0');

const now8601 = () => {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth()+1)}-${pad(n.getDate())}T${pad(n.getHours())}:${pad(n.getMinutes())}`;
};

const calcPickupState = (form) => {
  const inC  = +form.in_count  || 0;
  const outC = +form.out_count || 0;
  const inKg = +form.tub_weight || DQ;
  const grade = form.grade;
  const expP = +form.expected_pickup || GRADE_DEFAULTS[grade] || 113;
  if (!inC || !outC) return { outKg: null, ratio: null, alertState: 'def', expP, lo: expP-5, hi: expP+5 };
  const outKg = inKg * (inC / outC);
  const ratio = (outKg / inKg) * 100;
  const lo = expP - 5, hi = expP + 5;
  let alertState;
  if (ratio < lo || ratio > hi)          alertState = 'err';
  else if (ratio < lo+1.5 || ratio > hi-1.5) alertState = 'warn';
  else alertState = 'ok';
  return { outKg, ratio, alertState, expP, lo, hi };
};

const calcSoakStop = (start, hours) => {
  if (!start || !hours) return '';
  const dt = new Date(start);
  dt.setTime(dt.getTime() + hours * 3600000);
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

// ── Main Component ────────────────────────────────────────────────────────────
const SoakingDashboard = () => {
  const today = new Date().toISOString().slice(0,10);
  const navigate = useNavigate();

  const [sessionDate, setSessionDate] = useState(today);
  const [shift, setShift]             = useState('NIGHT');
  const [session, setSession]         = useState(null);
  const [entries, setEntries]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [lineFilter, setLineFilter]   = useState('');
  const [lotFilter, setLotFilter]     = useState('');
  const [toast, setToast]             = useState(null);

  // Modals
  const [addOpen, setAddOpen]         = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [form, setForm]               = useState({...EMPTY_FORM});
  const [reasonOpen, setReasonOpen]   = useState(false);
  const [reasonText, setReasonText]   = useState('');
  const [reasonCb, setReasonCb]       = useState(null);
  const [syncOpen, setSyncOpen]       = useState(false);
  const [syncLot, setSyncLot]         = useState(null);
  const [gradeDefaults, setGradeDefaults] = useState(GRADE_DEFAULTS);
  const [tubLines, setTubLines]           = useState([]);

  // Master dropdowns from DB
  const [ddProducts,   setDdProducts]   = useState(PRODUCTS_FB.map(n=>({id:n,name:n})));
  const [ddGrades,     setDdGrades]     = useState(GRADES_FB.map(n=>({id:n,name:n})));
  const [ddBrands,     setDdBrands]     = useState([]);
  const [ddPackSizes,  setDdPackSizes]  = useState([]);
  const [ddPackStyles, setDdPackStyles] = useState(PACK_STYLES_FB.map(n=>({id:n,name:n})));

  // Current user from JWT
  const [currentUser, setCurrentUser]   = useState(null);

  // Inline search state for master dropdowns
  const [srchProduct,  setSrchProduct]  = useState('');
  const [srchGrade,    setSrchGrade]    = useState('');
  const [srchBrand,    setSrchBrand]    = useState('');
  const [srchPackSz,   setSrchPackSz]   = useState('');
  const [srchPackSt,   setSrchPackSt]   = useState('');
  const [showDd,       setShowDd]       = useState({});  // which dropdown open

  // Modal overrideable session fields
  const [formDate,     setFormDate]     = useState('');
  const [formShift,    setFormShift]    = useState('');
  const [formDayCode,  setFormDayCode]  = useState('');

  const toastTimer = useRef(null);

  // ── Tub Line Helpers ───────────────────────────────────────────
  const emptyTubLine = (nextNo) => ({
    _id: Date.now() + Math.random(),
    lot_sfx: '', tub_no: nextNo, tub_weight: DQ, soaking_id: '',
    soak_start_time: now8601(),
    in_big_wt: '', in_small_wt: '', in_count: '',
    out_big_wt: '', out_small_wt: '', out_count: '',
    out_tub_wt: '', remarks: '',
  });

  const setTL = (id, key, val) => setTubLines(lines =>
    lines.map(l => l._id === id ? { ...l, [key]: val } : l)
  );

  const addTubLine = () => {
    const nextNo = tubLines.length
      ? Math.max(...tubLines.map(l => +l.tub_no || 0)) + 1
      : (entries.length ? Math.max(...entries.map(e => +e.tub_no || 0)) + 1 : 1);
    setTubLines(lines => [...lines, emptyTubLine(nextNo)]);
  };

  const removeTubLine = (id) => setTubLines(lines => lines.filter(l => l._id !== id));

  const calcLinePK = (line) => {
    const inC  = +line.in_count  || 0;
    const outC = +line.out_count || 0;
    const inKg = +line.tub_weight || DQ;
    const expP = +form.expected_pickup || GRADE_DEFAULTS[form.grade] || 113;
    if (!inC || !outC) return { ratio: null, alertState: 'def', chip: '⏳' };
    const outKg = inKg * (inC / outC);
    const ratio = (outKg / inKg) * 100;
    const lo = expP - 5, hi = expP + 5;
    let alertState;
    if (ratio < lo || ratio > hi)               alertState = 'err';
    else if (ratio < lo+1.5 || ratio > hi-1.5)  alertState = 'warn';
    else alertState = 'ok';
    const chip = alertState==='ok' ? '🟢' : alertState==='warn' ? '🟡' : '🔴';
    return { ratio, outKg, alertState, chip };
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  };

  // ── API ────────────────────────────────────────────────────────
  const api = useCallback(async (action, body = {}) => {
    const token = localStorage.getItem('token');
    const res   = await fetch(`${API_BASE}?action=${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'API error');
    return json.data;
  }, []);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('get_or_create_session', { session_date: sessionDate, shift });
      setSession(d.session);
      const e = await api('get_entries', { session_id: d.session.id });
      setEntries(e.entries || []);
    } catch (err) {
      showToast(err.message, 'error');
    } finally { setLoading(false); }
  }, [sessionDate, shift, api]);

  useEffect(() => { loadSession(); }, [loadSession]);

  useEffect(() => {
    api('get_grade_defaults').then(d => {
      const map = {};
      (d.defaults || []).forEach(g => { map[g.grade] = +g.expected_pickup; });
      if (Object.keys(map).length) setGradeDefaults(map);
    }).catch(() => {});
  }, [api]);

  // Load master dropdowns
  useEffect(() => {
    api('get_dropdowns').then(d => {
      const dd = d.dropdowns || {};
      if (dd.products?.length)    setDdProducts(dd.products);
      if (dd.grades?.length)      setDdGrades(dd.grades);
      if (dd.brands?.length)      setDdBrands(dd.brands);
      if (dd.pack_sizes?.length)  setDdPackSizes(dd.pack_sizes);
      if (dd.pack_styles?.length) setDdPackStyles(dd.pack_styles);
    }).catch(() => {});
  }, [api]);

  // Get current user from localStorage JWT
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const parts = token.split('.');
        const payload = JSON.parse(atob(parts[1].replace(/-/g,'+').replace(/_/g,'/')));
        setCurrentUser(payload);
      }
    } catch(e) {}
  }, []);

  // Permission helpers
  const isAdmin    = () => currentUser?.role === 'admin';
  const canEdit    = () => currentUser?.role === 'admin' || currentUser?.role === 'incharge' || currentUser?.role === 'supervisor';
  const canDelete  = () => isAdmin();
  const canSubmit  = () => !!currentUser; // any logged-in user

  // ── Form helpers ───────────────────────────────────────────────
  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const calcInBS  = (f) => f.in_small_wt  > 0 ? (f.in_big_wt  / f.in_small_wt).toFixed(3)  : '';
  const calcOutBS = (f) => f.out_small_wt > 0 ? (f.out_big_wt / f.out_small_wt).toFixed(3) : '';
  const calcH2P   = (f) => {
    if (f.dispatch_date && f.dispatch_time && f.unloading_date && f.unloading_time) {
      const h = (new Date(`${f.unloading_date}T${f.unloading_time}`) -
                 new Date(`${f.dispatch_date}T${f.dispatch_time}`)) / 3600000;
      return h > 0 ? +h.toFixed(2) : null;
    }
    return null;
  };

  const openAdd = () => {
    const nextTub = entries.length ? Math.max(...entries.map(e => +e.tub_no)) + 1 : 1;
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      tub_weight: DQ,
      soak_start_time: now8601(),
      expected_pickup: gradeDefaults['16/20'] || 114,
    });
    setFormDate(sessionDate);
    setFormShift(shift);
    setFormDayCode(session?.day_code || '');
    setSrchProduct(''); setSrchGrade(''); setSrchBrand(''); setSrchPackSz(''); setSrchPackSt('');
    setShowDd({});
    setTubLines([{
      _id: Date.now(),
      lot_sfx: '', tub_no: nextTub, tub_weight: DQ, soaking_id: '',
      soak_start_time: now8601(),
      in_big_wt: '', in_small_wt: '', in_count: '',
      out_big_wt: '', out_small_wt: '', out_count: '',
      out_tub_wt: '', remarks: '',
    }]);
    setAddOpen(true);
  };

  const openEdit = (id) => {
    const e = entries.find(x => +x.id === id);
    if (!e) return;
    setEditingId(id);
    setForm({
      lot_sfx:         e.lot_sfx         || '',
      packing_line:    e.packing_line,
      product:         e.product,
      grade:           e.grade,
      brand:           e.brand            || '',
      soaking_hours:   +e.soaking_hours,
      is_cooking:      !!+e.is_cooking,
      in_big_wt:       e.in_big_wt        || '',
      in_small_wt:     e.in_small_wt      || '',
      in_count:        e.in_count,
      tub_no:          e.tub_no,
      tub_weight:      e.tub_weight,
      soaking_id:      e.soaking_id        || '',
      soak_start_time: (e.soak_start_time || '').replace(' ','T'),
      remarks:         e.remarks           || '',
      out_big_wt:      e.out_big_wt        || '',
      out_small_wt:    e.out_small_wt      || '',
      out_count:       e.out_count,
      expected_pickup: e.expected_pickup,
      dispatch_date:   e.dispatch_date     || '',
      dispatch_time:   e.dispatch_time     || '',
      unloading_date:  e.unloading_date    || '',
      unloading_time:  e.unloading_time    || '',
    });
    setAddOpen(true);
  };

  // Determine stage from filled data
  const getStageForLine = (tl) => {
    if (tl.out_count && +tl.out_count > 0) return STAGES.OUT_FILLED;
    if (tl.in_count  && +tl.in_count  > 0) return STAGES.IN_FILLED;
    return STAGES.DRAFT;
  };

  const saveEntry = async () => {
    if (!form.lot_sfx)           return showToast('Lot No. suffix is required', 'error');
    if (tubLines.length === 0)   return showToast('Add at least one tub row', 'error');
    if (tubLines.some(l => !l.tub_no)) return showToast('Tub No. is required on all rows', 'error');

    if (editingId) {
      setAddOpen(false);
      setReasonText('');
      setReasonCb(() => async (reason) => {
        try {
          const tl = tubLines[0];
          const stage = getStageForLine(tl);
          await api('edit_entry', { ...buildPayload(tl), id: editingId, reason, entry_status: stage });
          showToast(`Tub #${tl.tub_no} updated ✓`);
          loadSession();
        } catch (err) { showToast(err.message, 'error'); }
      });
      setReasonOpen(true);
    } else {
      try {
        let saved = 0;
        for (const tl of tubLines) {
          const stage = getStageForLine(tl);
          await api('add_entry', { ...buildPayload(tl), entry_status: stage });
          saved++;
        }
        setAddOpen(false);
        showToast(`${saved} tub(s) saved ✓`);
        loadSession();
      } catch (err) { showToast(err.message, 'error'); }
    }
  };

  const buildPayload = (tl = {}) => ({
    session_id:      session.id,
    session_date_override: formDate !== sessionDate ? formDate : undefined,
    shift_override:  formShift !== shift ? formShift : undefined,
    day_code_override: formDayCode !== (session?.day_code||'') ? formDayCode : undefined,
    lot_sfx:         form.lot_sfx,
    packing_line:    form.is_cooking ? form.packing_line : 'VICTOR-1',
    product:         form.product,
    grade:           form.grade,
    brand:           form.brand,
    pack_size:       form.pack_size || '',
    soaking_hours:   form.soaking_hours,
    is_cooking:      form.is_cooking ? 1 : 0,
    in_big_wt:       tl.in_big_wt    || '',
    in_small_wt:     tl.in_small_wt  || '',
    in_count:        tl.in_count     || 0,
    lot_sfx:         tl.lot_sfx      || form.lot_sfx || '',
    tub_no:          tl.tub_no       || 1,
    tub_weight:      tl.tub_weight   || DQ,
    soaking_id:      tl.soaking_id   || '',
    soak_start_time: tl.soak_start_time || '',
    remarks:         tl.remarks      || '',
    out_big_wt:      tl.out_big_wt   || '',
    out_small_wt:    tl.out_small_wt || '',
    out_count:       tl.out_count    || 0,
    out_tub_wt:      tl.out_tub_wt   || '',
    expected_pickup: form.expected_pickup || (gradeDefaults[form.grade] || 113),
    dispatch_date:   form.dispatch_date,
    dispatch_time:   form.dispatch_time,
    unloading_date:  form.unloading_date,
    unloading_time:  form.unloading_time,
    h2p_hours:       calcH2P(form),
  });

  const updateEntryStatus = async (id, status) => {
    try {
      await api('update_entry_status', { id, entry_status: status });
      if (status === STAGES.SUBMITTED) {
        showToast('Entry submitted & synced to Cold Store ✓', 'success');
      } else {
        showToast(`Stage updated to ${STAGE_LABEL[status]} ✓`);
      }
      loadSession();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const deleteEntry = (id) => {
    const e = entries.find(x => +x.id === id);
    if (!e) return;
    if (+e.synced) return showToast('Cannot delete a synced entry', 'error');
    setReasonText('');
    setReasonCb(() => async (reason) => {
      try {
        await api('delete_entry', { id, reason });
        showToast(`Tub #${e.tub_no} deleted`);
        loadSession();
      } catch (err) { showToast(err.message, 'error'); }
    });
    setReasonOpen(true);
  };

  const confirmReason = async () => {
    if (!reasonText.trim()) return showToast('Reason is mandatory', 'error');
    setReasonOpen(false);
    if (reasonCb) { await reasonCb(reasonText); setReasonCb(null); }
  };

  const openSyncModal = (lot) => { setSyncLot(lot); setSyncOpen(true); };

  const confirmSync = async () => {
    setSyncOpen(false);
    try {
      const d = await api('sync_lot', { session_id: session.id, lot_no: syncLot });
      showToast(`${d.synced_count} tub(s) synced to Cold Store ✓`);
      loadSession();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const syncAll = async () => {
    try {
      const d = await api('sync_session', { session_id: session.id });
      showToast(`${d.synced_count} entries synced (${d.lots_processed} lots) ✓`);
      loadSession();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const completeSession = async () => {
    try {
      await api('complete_session', { session_id: session.id });
      setSession(s => ({ ...s, status: 'COMPLETED' }));
      showToast('Session marked COMPLETED ✓');
    } catch (err) { showToast(err.message, 'error'); }
  };

  // ── Derived data ───────────────────────────────────────────────
  const filtered = entries.filter(e =>
    (!lineFilter || e.packing_line === lineFilter) &&
    (!lotFilter  || e.lot_no === lotFilter)
  );

  const lotGroups = filtered.reduce((acc, e) => {
    (acc[e.lot_no] = acc[e.lot_no] || []).push(e);
    return acc;
  }, {});

  const allLots = [...new Set(entries.map(e => e.lot_no))];

  const stats = {
    tubs:    entries.length,
    inputKg: entries.reduce((s,e) => s + +e.tub_weight, 0),
    outKg:   entries.reduce((s,e) => s + +e.output_kg, 0),
    avgPick: entries.length ? entries.reduce((s,e) => s + +e.pickup_ratio, 0) / entries.length : 0,
    pending: [...new Set(entries.filter(e => !+e.synced).map(e => e.lot_no))].length,
    alerts:  entries.filter(e => e.alert_state === 'err').length,
  };

  // ── Pickup state for live form ─────────────────────────────────
  const pk = calcPickupState({ ...form, expected_pickup: form.expected_pickup || gradeDefaults[form.grade] });

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="sk-root">

      {/* HEADER */}
      <div className="sk-header">
        <div className="sk-hdr-left">
          <button className="sk-back-btn" onClick={() => navigate('/modules')}>← Modules</button>
          <span className="sk-title">🪣 Soaking Dashboard</span>
          <span className="sk-role-badge">BAP-CCC4</span>
        </div>
        <div className="sk-hdr-center">
          <input type="date" className="sk-date-pick" value={sessionDate}
            onChange={e => setSessionDate(e.target.value)} />
          <div className="sk-shift-toggle">
            <button className={`sk-shift-btn ${shift==='DAY'?'active':''}`}  onClick={() => setShift('DAY')}>☀ DAY</button>
            <button className={`sk-shift-btn ${shift==='NIGHT'?'active':''}`} onClick={() => setShift('NIGHT')}>🌙 NIGHT</button>
          </div>
        </div>
        <div className="sk-hdr-right">
          {session && (
            <>
              <span className={`sk-day-code`}>Day Code: <strong>{session.day_code}</strong></span>
              <button className="sk-hdr-btn" onClick={() => showToast('Exporting…','info')}>⬇ Export</button>
            </>
          )}
        </div>
      </div>

      {/* FLOW BANNER */}
      <div className="sk-flow-banner">
        <div className="sk-flow-inner">
          <span className="sk-fn">ROMEO</span><span className="sk-fa">→</span>
          <span className="sk-fn">H2P Transit</span><span className="sk-fa">→</span>
          <span className="sk-fn sk-fn-active">🪣 SOAKING</span><span className="sk-fa">→</span>
          <span className="sk-fn sk-fn-decision">IQF?</span>
          <div className="sk-flow-branch">
            <div className="sk-branch-row"><span className="sk-bl">Yes</span><span className="sk-fn sk-fn-v23">VICTOR-2&3</span></div>
            <div className="sk-branch-row"><span className="sk-bl">No</span><span className="sk-fn sk-fn-v1">VICTOR-1</span></div>
          </div>
          <span className="sk-fa">→</span>
          <span className="sk-fn sk-fn-cs">CS Inventory</span>
        </div>
      </div>

      <div className="sk-main">

        {/* STATS */}
        <div className="sk-stats-grid">
          <div className="sk-stat-card sk-stat-blue">
            <span className="sk-stat-icon">🪣</span>
            <div><div className="sk-stat-lbl">Total Tubs</div><div className="sk-stat-val blue">{stats.tubs}</div></div>
          </div>
          <div className="sk-stat-card sk-stat-blue">
            <span className="sk-stat-icon">⚖️</span>
            <div><div className="sk-stat-lbl">Input KG</div><div className="sk-stat-val blue">{stats.inputKg.toLocaleString()}</div></div>
          </div>
          <div className="sk-stat-card sk-stat-green">
            <span className="sk-stat-icon">📦</span>
            <div><div className="sk-stat-lbl">Output KG</div><div className="sk-stat-val green">{stats.outKg.toFixed(0)}</div></div>
          </div>
          <div className="sk-stat-card sk-stat-green">
            <span className="sk-stat-icon">📊</span>
            <div><div className="sk-stat-lbl">Avg Pickup</div><div className="sk-stat-val green">{stats.tubs ? stats.avgPick.toFixed(1)+'%' : '—'}</div></div>
          </div>
          <div className={`sk-stat-card ${stats.alerts ? 'sk-stat-red' : 'sk-stat-amber'}`}>
            <span className="sk-stat-icon">{stats.alerts ? '🔴' : '⏳'}</span>
            <div>
              <div className="sk-stat-lbl">{stats.alerts ? 'Pickup Alerts' : 'Pending Sync'}</div>
              <div className={`sk-stat-val ${stats.alerts ? 'red' : 'amber'}`}>{stats.alerts || `${stats.pending} lots`}</div>
            </div>
          </div>
        </div>

        {/* SESSION CARD */}
        {session && (
          <div className="sk-session-card">
            <div className="sk-sess-field">
              <div className="sk-sess-lbl">SESSION DATE</div>
              <div className="sk-sess-val">{new Date(sessionDate).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}</div>
            </div>
            <div className="sk-sess-divider" />
            <div className="sk-sess-field">
              <div className="sk-sess-lbl">SHIFT</div>
              <div className="sk-sess-val">{shift}</div>
            </div>
            <div className="sk-sess-divider" />
            <div className="sk-sess-field">
              <div className="sk-sess-lbl">DAY CODE</div>
              <div className="sk-sess-val mono">{session.day_code}</div>
            </div>
            <div className="sk-sess-divider" />
            <div className="sk-sess-field">
              <div className="sk-sess-lbl">BAP CODE</div>
              <div className="sk-sess-val mono">{session.bap_code}</div>
            </div>
            <div className="sk-sess-divider" />
            <div className="sk-sess-field">
              <div className="sk-sess-lbl">STATUS</div>
              <div className={`sk-status-pill ${session.status === 'COMPLETED' ? 'sp-done' : 'sp-open'}`}>
                {session.status}
              </div>
            </div>
            <div className="sk-sess-actions">
              {session.status === 'OPEN' && (
                <>
                  <button className="sk-btn sk-btn-add"      onClick={openAdd}>+ Add Tub</button>
                  <button className="sk-btn sk-btn-complete" onClick={completeSession}>✓ Complete</button>
                  <button className="sk-btn sk-btn-sync"     onClick={syncAll}>🔄 Sync All</button>
                </>
              )}
              <button className="sk-btn sk-btn-export" onClick={() => showToast('Exporting…','info')}>⬇ Export</button>
            </div>
          </div>
        )}

        {/* TABLE CARD */}
        <div className="sk-tbl-card">
          <div className="sk-tbl-header">
            <span className="sk-tbl-title">
              Soaking Entries — {new Date(sessionDate).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})} · {shift === 'DAY' ? 'Day' : 'Night'} Shift
            </span>
            <div className="sk-tbl-actions">
              <select className="sk-filter-sel" value={lineFilter} onChange={e => setLineFilter(e.target.value)}>
                <option value="">All Lines</option>
                <option>VICTOR-1</option><option>VICTOR-2</option><option>VICTOR-3</option>
              </select>
              <select className="sk-filter-sel" value={lotFilter} onChange={e => setLotFilter(e.target.value)}>
                <option value="">All Lots</option>
                {allLots.map(l => <option key={l} value={l}>{l.slice(-4)}</option>)}
              </select>
              <button className="sk-btn sk-btn-export" onClick={() => showToast('Opening audit log…','info')}>📜 Audit</button>
            </div>
          </div>

          {loading && <div className="sk-loading">Loading…</div>}

          {!loading && Object.keys(lotGroups).length === 0 && (
            <div className="sk-empty">
              <div className="sk-empty-icon">🪣</div>
              <div className="sk-empty-text">No tub entries yet</div>
              <div className="sk-empty-sub">Click "Add Tub" to start recording soaking data</div>
            </div>
          )}

          {!loading && Object.entries(lotGroups).map(([lot, rows]) => {
            const allSynced = rows.every(r => +r.synced);
            const tInKg     = rows.reduce((s,r) => s + +r.tub_weight, 0);
            const tOutKg    = rows.reduce((s,r) => s + +r.output_kg, 0);
            const avgP      = rows.reduce((s,r) => s + +r.pickup_ratio, 0) / rows.length;
            const hasErr    = rows.some(r => r.alert_state === 'err');
            const firstCsId = rows.find(r => r.cs_inventory_id)?.cs_inventory_id;
            return (
              <div key={lot} className="sk-lot-group">
                <div className="sk-lot-bar">
                  <span className="sk-lot-tag">{lot}</span>
                  <span className="sk-lot-meta">{rows[0].product} · {rows[0].grade} · <strong>{rows.length} tubs</strong></span>
                  <span className="sk-lot-meta">Input: <strong>{tInKg.toLocaleString()} KG</strong></span>
                  <span className="sk-lot-meta">Output: <strong>{tOutKg.toFixed(1)} KG</strong></span>
                  <span className="sk-lot-meta">Avg Pickup: <strong>{avgP.toFixed(1)}%</strong></span>
                  {hasErr && <span className="sk-alert-badge">⚠ ALERT</span>}
                  {allSynced
                    ? <span className="sk-sync-btn sk-synced" style={{marginLeft:'auto'}}>✓ Synced ({firstCsId})</span>
                    : <button className="sk-sync-btn sk-pending" style={{marginLeft:'auto'}} onClick={() => openSyncModal(lot)}>🔄 Sync to CS</button>
                  }
                </div>

                <div className="sk-tbl-wrap">
                  <table className="sk-tbl">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th className="th-std">Tub</th>
                        <th className="th-std">Line</th>
                        <th className="th-std">Product</th>
                        <th className="th-std">Grade</th>
                        <th className="th-std">Tub Wt</th>
                        <th>IN Cnt</th><th>IN B/S</th>
                        <th>OUT Cnt</th><th>OUT B/S</th>
                        <th className="th-comp">Pickup %</th>
                        <th className="th-comp">Range</th>
                        <th className="th-time">Start</th>
                        <th className="th-time">Stop</th>
                        <th className="th-comp">Hrs</th>
                        <th className="th-comp">H2P</th>
                        <th className="th-comp">Out KG</th>
                        <th className="th-bridge">Brand</th>
                        <th className="th-route">Route</th>
                        <th>CS Sync</th>
                        <th style={{background:'#f0fdf4',color:'#059669',minWidth:'70px'}}>Stage</th>
                        <th className="th-act">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const ref  = +r.expected_pickup || gradeDefaults[r.grade] || 113;
                        const lo   = ref - 5, hi = ref + 5;
                        const pCls = r.alert_state === 'err' ? 'td-red' : r.alert_state === 'warn' ? 'td-amber' : 'td-grn';
                        return (
                          <tr key={r.id} className={+r.synced ? 'row-synced' : 'row-pending'}>
                            <td className="td-mono muted">{i+1}</td>
                            <td><strong>{r.tub_no}</strong></td>
                            <td>{r.packing_line}</td>
                            <td className="fw6">{r.product}</td>
                            <td>{r.grade}</td>
                            <td className="td-mono">{r.tub_weight}</td>
                            <td className="td-mono">{r.in_count}</td>
                            <td className="td-mono">{r.in_bs_ratio || '—'}</td>
                            <td className="td-mono">{r.out_count}</td>
                            <td className="td-mono">{r.out_bs_ratio || '—'}</td>
                            <td className={`td-comp ${pCls}`}>{(+r.pickup_ratio).toFixed(2)}%</td>
                            <td className="td-comp" style={{fontSize:'10px'}}>{lo.toFixed(1)}–{hi.toFixed(1)}%</td>
                            <td className="td-mono" style={{fontSize:'10px'}}>{(r.soak_start_time||'').slice(11,16)||'—'}</td>
                            <td className="td-mono" style={{fontSize:'10px'}}>{(r.soak_stop_time ||'').slice(11,16)||'—'}</td>
                            <td className="td-comp">{r.soaking_hours}h</td>
                            <td className="td-comp">{r.h2p_hours ? r.h2p_hours+'h' : '—'}</td>
                            <td className="td-comp"><strong>{(+r.output_kg).toFixed(2)}</strong></td>
                            <td className="td-bridge" style={{fontSize:'10px'}}>{r.brand || '—'}</td>
                            <td>
                              {+r.is_cooking
                                ? <span className="rc rc-ifq">❄ IQF→V2&3</span>
                                : r.packing_line === 'VICTOR-1'
                                  ? <span className="rc rc-v1">V-1</span>
                                  : <span className="rc rc-v23">V-2&3</span>
                              }
                            </td>
                            <td>
                              {+r.synced
                                ? <span className="sync-chip sc-ok">✓ {r.cs_inventory_id}</span>
                                : <span className="sync-chip sc-pend">⏳ Pending</span>
                              }
                            </td>
                            <td style={{textAlign:'center'}}>
                              <span style={{
                                background: (STAGE_COLOR[r.entry_status]||'#9ca3af')+'20',
                                color: STAGE_COLOR[r.entry_status]||'#9ca3af',
                                border: `1px solid ${STAGE_COLOR[r.entry_status]||'#9ca3af'}`,
                                borderRadius:'4px', padding:'2px 6px',
                                fontSize:'9px', fontWeight:'700', whiteSpace:'nowrap'
                              }}>
                                {STAGE_LABEL[r.entry_status]||'Draft'}
                              </span>
                            </td>
                            <td>
                              <div className="act-cell" style={{flexWrap:'wrap',gap:'3px'}}>
                                {canEdit() && r.entry_status !== STAGES.SUBMITTED && (
                                  <button className="btn-sm btn-edit" onClick={() => openEdit(+r.id)}>✏ Edit</button>
                                )}
                                {canSubmit() && r.entry_status === STAGES.OUT_FILLED && (
                                  <button className="btn-sm btn-submit" onClick={() => updateEntryStatus(+r.id, STAGES.SUBMITTED)} title="Submit & sync to CS">▶ Submit</button>
                                )}
                                {canDelete() && r.entry_status !== STAGES.SUBMITTED && (
                                  <button className="btn-sm btn-del" onClick={() => deleteEntry(+r.id)}>🗑</button>
                                )}
                                {r.entry_status === STAGES.SUBMITTED && (
                                  <span style={{color:'#059669',fontSize:'9px',fontWeight:'700'}}>✓ Synced</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} style={{textAlign:'left',paddingLeft:'14px'}}>Lot Totals</td>
                        <td>{tInKg.toLocaleString()}</td>
                        <td colSpan={4}>—</td>
                        <td className="td-comp" style={{color:'#059669'}}>{avgP.toFixed(2)}%</td>
                        <td colSpan={5}>—</td>
                        <td className="td-comp" style={{color:'#1565c0'}}>{tOutKg.toFixed(2)}</td>
                        <td colSpan={7}>—</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── ADD/EDIT MODAL ──────────────────────────────────────── */}
      {addOpen && (
        <div className="sk-modal-overlay" onClick={e => e.target === e.currentTarget && setAddOpen(false)}>
          <div className="sk-modal sk-modal-lg">
            <div className="sk-modal-header">
              <span className="sk-modal-title">{editingId ? `Edit Tub Entry #${form.tub_no}` : 'Add Tub Entry'}</span>
              <button className="sk-modal-close" onClick={() => setAddOpen(false)}>✕</button>
            </div>
            <div className="sk-modal-body">

              {/* ═══ SECTION 1 — HEADER INFORMATION ═══ */}
              <div className="sk-section-hdr">
                <span className="sk-section-hdr-icon">📋</span>
                <span>HEADER INFORMATION</span>
                <span className="sk-section-hdr-sub">Session · Product · H2P · Soaking IN (filled once per lot)</span>
              </div>

              {/* Zone 1 — Session Info (EDITABLE) */}
              <div className="sk-ez">
                <div className="sk-ez-hdr sk-ez-sess">📋 Session Info</div>
                <div className="sk-ez-body">
                  <div className="sk-fg sk-fg-4">
                    <div className="sk-fld">
                      <div className="sk-flbl">Date <span style={{color:'#d97706',fontSize:'9px'}}>✎ Editable</span></div>
                      <input type="date" className="sk-finp" value={formDate} onChange={e => setFormDate(e.target.value)} />
                    </div>
                    <div className="sk-fld">
                      <div className="sk-flbl">Shift <span style={{color:'#d97706',fontSize:'9px'}}>✎ Editable</span></div>
                      <select className="sk-finp sk-sel" value={formShift} onChange={e => setFormShift(e.target.value)}>
                        <option value="MORNING">MORNING</option>
                        <option value="GENERAL">GENERAL</option>
                        <option value="NIGHT">NIGHT</option>
                        <option value="DAY">DAY</option>
                      </select>
                    </div>
                    <div className="sk-fld">
                      <div className="sk-flbl">Day Code <span style={{color:'#d97706',fontSize:'9px'}}>✎ Editable</span></div>
                      <input className="sk-finp mono" value={formDayCode} onChange={e => setFormDayCode(e.target.value)} placeholder="e.g. 6D09" />
                    </div>
                    <div className="sk-fld">
                      <div className="sk-flbl">BAP Code</div>
                      <input className="sk-finp sk-lk mono" readOnly value="BAP-CCC4" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Zone H2P — Dispatch & Unloading (TOP of product section) */}
              <div className="sk-ez">
                <div className="sk-ez-hdr" style={{background:'linear-gradient(90deg,#0d47a1,#1565c0)'}}>🚛 H2P — Harvest to Processing (Dispatch → Unloading)</div>
                <div className="sk-ez-body">
                  <div className="sk-fg sk-fg-4" style={{alignItems:'end'}}>
                    <div className="sk-fld"><div className="sk-flbl">Dispatch Date</div><input type="date" className="sk-finp" value={form.dispatch_date} onChange={e => setF('dispatch_date',e.target.value)} /></div>
                    <div className="sk-fld"><div className="sk-flbl">Dispatch Time</div><input type="time" className="sk-finp" value={form.dispatch_time} onChange={e => setF('dispatch_time',e.target.value)} /></div>
                    <div className="sk-fld"><div className="sk-flbl">Unloading Date</div><input type="date" className="sk-finp" value={form.unloading_date} onChange={e => setF('unloading_date',e.target.value)} /></div>
                    <div className="sk-fld"><div className="sk-flbl">Unloading Time</div><input type="time" className="sk-finp" value={form.unloading_time} onChange={e => setF('unloading_time',e.target.value)} /></div>
                  </div>
                  <div style={{marginTop:'8px',display:'flex',alignItems:'center',gap:'12px'}}>
                    <div className="sk-flbl">Total H2P (hrs)</div>
                    <input className="sk-finp sk-comp mono" readOnly
                      value={calcH2P(form) ? calcH2P(form) + ' hrs' : '— Fill dates above'}
                      style={{width:'180px', fontSize:'14px', fontWeight:'700'}} />
                    {calcH2P(form) && (
                      <span style={{fontSize:'11px',color: +calcH2P(form) > 48 ? '#dc2626' : '#059669', fontWeight:'600'}}>
                        {+calcH2P(form) > 48 ? '⚠ Over 48 hrs — check dates' : '✓ Within range'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Zone 2 — Product Identity */}
              <div className="sk-ez">
                <div className="sk-ez-hdr sk-ez-prod">🦐 Product Identity</div>
                <div className="sk-ez-body">
                  {/* Cooking toggle */}
                  <div className="sk-cook-row">
                    <span className="sk-flbl">IQF / Cooking Item?</span>
                    <div className={`sk-cook-toggle ${form.is_cooking ? 'on' : ''}`} onClick={() => {
                      const next = !form.is_cooking;
                      setForm(f => ({ ...f, is_cooking: next, packing_line: next ? 'VICTOR-2' : 'VICTOR-1' }));
                    }}>
                      <div className="sk-cook-knob" />
                    </div>
                    <span className="sk-cook-label" style={{color: form.is_cooking ? '#7c3aed' : '#075985'}}>
                      {form.is_cooking ? 'Yes — IQF route: VICTOR-2 or VICTOR-3' : 'No — VICTOR-1 only'}
                    </span>
                  </div>

                  <div className="sk-fg sk-fg-3" style={{marginTop:'10px'}}>


                    {/* Product — inline search */}
                    <div className="sk-fld" style={{position:'relative'}}>
                      <div className="sk-flbl">Product <span className="sk-req">*</span></div>
                      <input className="sk-finp" placeholder="Search product…"
                        value={srchProduct || form.product}
                        onChange={e => { setSrchProduct(e.target.value); setShowDd(d=>({...d,product:true})); }}
                        onFocus={() => { setSrchProduct(''); setShowDd(d=>({...d,product:true})); }}
                        onBlur={() => setTimeout(()=>setShowDd(d=>({...d,product:false})),200)}
                      />
                      {showDd.product && (
                        <div className="sk-dd-list">
                          {ddProducts.filter(p=>p.name.toLowerCase().includes((srchProduct||'').toLowerCase())).map(p=>(
                            <div key={p.id} className="sk-dd-item" onMouseDown={()=>{ setF('product',p.name); setSrchProduct(''); setShowDd(d=>({...d,product:false})); }}>
                              {p.name}
                            </div>
                          ))}
                        </div>
                      )}
                      {form.product && <div className="sk-fhint" style={{color:'#059669'}}>✓ {form.product}</div>}
                    </div>

                    {/* Grade — inline search */}
                    <div className="sk-fld" style={{position:'relative'}}>
                      <div className="sk-flbl">Grade <span className="sk-req">*</span></div>
                      <input className="sk-finp" placeholder="Search grade…"
                        value={srchGrade || form.grade}
                        onChange={e => { setSrchGrade(e.target.value); setShowDd(d=>({...d,grade:true})); }}
                        onFocus={() => { setSrchGrade(''); setShowDd(d=>({...d,grade:true})); }}
                        onBlur={() => setTimeout(()=>setShowDd(d=>({...d,grade:false})),200)}
                      />
                      {showDd.grade && (
                        <div className="sk-dd-list">
                          {ddGrades.filter(g=>g.name.toLowerCase().includes((srchGrade||'').toLowerCase())).map(g=>(
                            <div key={g.id} className="sk-dd-item" onMouseDown={()=>{ const gv=g.name; setForm(f=>({...f,grade:gv,expected_pickup:gradeDefaults[gv]||113})); setSrchGrade(''); setShowDd(d=>({...d,grade:false})); }}>
                              {g.name}
                            </div>
                          ))}
                        </div>
                      )}
                      {form.grade && <div className="sk-fhint" style={{color:'#059669'}}>✓ {form.grade}</div>}
                    </div>
                  </div>

                  <div className="sk-fg sk-fg-3" style={{marginTop:'8px'}}>
                    {/* Packing Line */}
                    <div className="sk-fld">
                      <div className="sk-flbl">Packing Line <span className="sk-req">*</span></div>
                      <select className="sk-finp sk-sel" value={form.packing_line} onChange={e => setF('packing_line', e.target.value)}>
                        {(form.is_cooking ? ['VICTOR-2','VICTOR-3'] : ['VICTOR-1']).map(v => <option key={v}>{v}</option>)}
                      </select>
                      <div className="sk-fhint" style={{color: form.is_cooking ? '#7c3aed' : '#075985'}}>
                        {form.is_cooking ? 'Cooking → V2 or V3' : 'Non-cooking → V1 only'}
                      </div>
                    </div>

                    {/* Soaking Hours */}
                    <div className="sk-fld">
                      <div className="sk-flbl">Soaking Hours <span className="sk-req">*</span></div>
                      <input className="sk-finp" type="number" step="0.5" value={form.soaking_hours} onChange={e => setF('soaking_hours', e.target.value)} />
                    </div>


                  </div>

                  {/* Brand / Pack Size — bridge fields passed to CS */}
                  <div className="sk-fg sk-fg-3" style={{marginTop:'8px'}}>
                    {/* Brand */}
                    <div className="sk-fld" style={{position:'relative'}}>
                      <div className="sk-flbl">Brand <span className="sk-ftag">Bridge→CS</span></div>
                      <input className="sk-finp sk-bridge" placeholder="Search brand…"
                        value={srchBrand || form.brand}
                        onChange={e=>{ setSrchBrand(e.target.value); setShowDd(d=>({...d,brand:true})); }}
                        onFocus={()=>{ setSrchBrand(''); setShowDd(d=>({...d,brand:true})); }}
                        onBlur={()=>setTimeout(()=>setShowDd(d=>({...d,brand:false})),200)}
                      />
                      {showDd.brand && (
                        <div className="sk-dd-list">
                          {ddBrands.filter(b=>b.name.toLowerCase().includes((srchBrand||'').toLowerCase())).map(b=>(
                            <div key={b.id} className="sk-dd-item" onMouseDown={()=>{ setF('brand',b.name); setSrchBrand(''); setShowDd(d=>({...d,brand:false})); }}>{b.name}</div>
                          ))}
                        </div>
                      )}
                      {form.brand && <div className="sk-fhint" style={{color:'#059669'}}>✓ {form.brand}</div>}
                    </div>



                    {/* Pack Size */}
                    <div className="sk-fld" style={{position:'relative'}}>
                      <div className="sk-flbl">Pack Size <span className="sk-ftag">Bridge→CS</span></div>
                      <input className="sk-finp sk-bridge" placeholder="Search pack size…"
                        value={srchPackSz || form.pack_size}
                        onChange={e=>{ setSrchPackSz(e.target.value); setShowDd(d=>({...d,packSz:true})); }}
                        onFocus={()=>{ setSrchPackSz(''); setShowDd(d=>({...d,packSz:true})); }}
                        onBlur={()=>setTimeout(()=>setShowDd(d=>({...d,packSz:false})),200)}
                      />
                      {showDd.packSz && (
                        <div className="sk-dd-list">
                          {ddPackSizes.filter(p=>p.name.toLowerCase().includes((srchPackSz||'').toLowerCase())).map(p=>(
                            <div key={p.id} className="sk-dd-item" onMouseDown={()=>{ setF('pack_size',p.name); setSrchPackSz(''); setShowDd(d=>({...d,packSz:false})); }}>{p.name}</div>
                          ))}
                        </div>
                      )}
                      {form.pack_size && <div className="sk-fhint" style={{color:'#059669'}}>✓ {form.pack_size}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* ═══ SECTION 2 — LINE ITEMS (multiple tubs) ═══ */}
              <div className="sk-section-line">
                <span className="sk-section-line-icon">🪣</span>
                <span>LINE ITEMS — TUB DETAILS</span>
                <span className="sk-section-line-sub">Add one row per tub · Soaking OUT &amp; Pickup calculated automatically</span>
                <button className="sk-btn-add-line" onClick={addTubLine}>+ Add Tub Row</button>
              </div>

              <div className="sk-ez">
                <div className="sk-tl-wrap">
                  <table className="sk-tl-tbl">
                    <thead>
                      <tr>
                        <th rowSpan={2} className="sk-th-base">S.No</th>
                        <th rowSpan={2} className="sk-th-key">Lot Suffix *</th>
                        <th rowSpan={2} className="sk-th-key">Tub No. *</th>
                        <th rowSpan={2} className="sk-th-base">Tub Wt (KG)</th>
                        <th colSpan={4} className="sk-th-in">Soaking IN B/S Ratio</th>
                        <th rowSpan={2} className="sk-th-base">Soaking ID</th>
                        <th rowSpan={2} className="sk-th-base">Start Time *</th>
                        <th rowSpan={2} className="sk-th-auto">Stop Time</th>
                        <th colSpan={5} className="sk-th-out">Soaking OUT B/S Ratio</th>
                        <th rowSpan={2} className="sk-th-auto">Pickup %</th>
                        <th rowSpan={2} className="sk-th-auto">Alert</th>
                        <th rowSpan={2} className="sk-th-base">Remarks</th>
                        <th rowSpan={2} style={{width:'32px'}}></th>
                      </tr>
                      <tr>
                        <th className="sk-th-in">Big Wt (g)</th>
                        <th className="sk-th-in">Small Wt (g)</th>
                        <th className="sk-th-in">IN B/S</th>
                        <th className="sk-th-in">IN Count /454g *</th>
                        <th className="sk-th-out">OUT Big (g)</th>
                        <th className="sk-th-out">OUT Small (g)</th>
                        <th className="sk-th-out">OUT B/S</th>
                        <th className="sk-th-out">OUT Count *</th>
                        <th className="sk-th-out">OUT Tub Wt (KG)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tubLines.map((tl, idx) => {
                        const pk2  = calcLinePK(tl);
                        const inBS = (+tl.in_small_wt > 0) ? (+tl.in_big_wt / +tl.in_small_wt).toFixed(3) : '';
                        const outBS = (+tl.out_small_wt > 0) ? (+tl.out_big_wt / +tl.out_small_wt).toFixed(3) : '';
                        const stopT = calcSoakStop(tl.soak_start_time, form.soaking_hours);
                        return (
                          <tr key={tl._id} className={`sk-tl-row pk-row-${pk2.alertState}`}>
                            <td style={{textAlign:'center',color:'#6b7280',fontSize:'10px'}}>{idx+1}</td>
                            <td><input className="sk-tl-inp sk-tl-key mono" value={tl.lot_sfx} onChange={e => setTL(tl._id,'lot_sfx',e.target.value)} placeholder="4-digit" maxLength={4} /></td>
                            <td><input className="sk-tl-inp sk-tl-key" type="number" value={tl.tub_no} onChange={e => setTL(tl._id,'tub_no',e.target.value)} placeholder="1" /></td>
                            <td><input className="sk-tl-inp" type="number" step="0.5" value={tl.tub_weight} onChange={e => setTL(tl._id,'tub_weight',e.target.value)} /></td>
                            <td className="sk-td-in"><input className="sk-tl-inp" type="number" value={tl.in_big_wt} onChange={e => setTL(tl._id,'in_big_wt',e.target.value)} placeholder="80" /></td>
                            <td className="sk-td-in"><input className="sk-tl-inp" type="number" value={tl.in_small_wt} onChange={e => setTL(tl._id,'in_small_wt',e.target.value)} placeholder="64" /></td>
                            <td className="sk-td-in"><input className="sk-tl-inp sk-tl-auto mono" readOnly value={inBS} /></td>
                            <td className="sk-td-in"><input className="sk-tl-inp" type="number" value={tl.in_count} onChange={e => setTL(tl._id,'in_count',e.target.value)} placeholder="33" /></td>
                            <td><input className="sk-tl-inp" value={tl.soaking_id} onChange={e => setTL(tl._id,'soaking_id',e.target.value)} placeholder="Batch" /></td>
                            <td><input className="sk-tl-inp" type="datetime-local" value={tl.soak_start_time} onChange={e => setTL(tl._id,'soak_start_time',e.target.value)} /></td>
                            <td><input className="sk-tl-inp sk-tl-auto mono" readOnly value={stopT} /></td>
                            <td className="sk-td-out"><input className="sk-tl-inp" type="number" value={tl.out_big_wt} onChange={e => setTL(tl._id,'out_big_wt',e.target.value)} placeholder="82" /></td>
                            <td className="sk-td-out"><input className="sk-tl-inp" type="number" value={tl.out_small_wt} onChange={e => setTL(tl._id,'out_small_wt',e.target.value)} placeholder="66" /></td>
                            <td className="sk-td-out"><input className="sk-tl-inp sk-tl-auto mono" readOnly value={outBS} /></td>
                            <td className="sk-td-out"><input className="sk-tl-inp" type="number" value={tl.out_count} onChange={e => setTL(tl._id,'out_count',e.target.value)} placeholder="28" /></td>
                            <td className="sk-td-out"><input className="sk-tl-inp" type="number" step="0.1" value={tl.out_tub_wt} onChange={e => setTL(tl._id,'out_tub_wt',e.target.value)} placeholder="KG" /></td>
                            <td><div className={`sk-tl-pk pk-val-${pk2.alertState}`}>{pk2.ratio !== null ? pk2.ratio.toFixed(1)+'%' : '—'}</div></td>
                            <td style={{textAlign:'center',fontSize:'15px'}}>{pk2.chip || '⏳'}</td>
                            <td><input className="sk-tl-inp" value={tl.remarks} onChange={e => setTL(tl._id,'remarks',e.target.value)} placeholder="Notes" /></td>
                            <td>{tubLines.length > 1 && <button className="sk-tl-del" onClick={() => removeTubLine(tl._id)}>✕</button>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {tubLines.length === 0 && (
                    <div className="sk-tl-empty">Click "+ Add Tub Row" to start adding tubs</div>
                  )}
                </div>
              </div>

            </div>
            <div className="sk-modal-footer">
              <button className="sk-btn sk-btn-export" onClick={() => setAddOpen(false)}>Cancel</button>
              <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                <span style={{fontSize:'11px',color:'#6b7280'}}>
                  {tubLines.every(l=>+l.out_count>0) ? '✓ OUT data complete — ready to submit' : 'Fill OUT Count to enable Submit'}
                </span>
                <button className="sk-btn sk-btn-add" onClick={saveEntry}>💾 Save Draft</button>
                <button
                  className="sk-btn sk-btn-submit-modal"
                  disabled={!tubLines.every(l=>+l.out_count>0 && +l.in_count>0)}
                  onClick={async () => {
                    await saveEntry();
                  }}
                  title="Save and submit all tubs to Cold Store"
                  style={{opacity: tubLines.every(l=>+l.out_count>0 && +l.in_count>0) ? 1 : 0.5}}
                >
                  ▶ Save & Submit to CS
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── REASON MODAL ──────────────────────────────────────────── */}
      {reasonOpen && (
        <div className="sk-modal-overlay" onClick={e => e.target === e.currentTarget && setReasonOpen(false)}>
          <div className="sk-modal sk-modal-sm">
            <div className="sk-modal-header">
              <span className="sk-modal-title">Reason Required</span>
              <button className="sk-modal-close" onClick={() => setReasonOpen(false)}>✕</button>
            </div>
            <div className="sk-modal-body" style={{padding:'16px 20px'}}>
              <p style={{fontSize:'12px',color:'#6b7280',marginBottom:'10px'}}>
                Every change to a soaking entry must be logged. Please provide a clear reason.
              </p>
              <div className="sk-fld">
                <div className="sk-flbl">Reason for Change <span className="sk-req">*</span></div>
                <textarea className="sk-reason-inp" value={reasonText} onChange={e => setReasonText(e.target.value)}
                  placeholder="e.g. Count corrected after recount…" rows={3} />
              </div>
            </div>
            <div className="sk-modal-footer">
              <button className="sk-btn sk-btn-export" onClick={() => setReasonOpen(false)}>Cancel</button>
              <button className="sk-btn sk-btn-add"    onClick={confirmReason}>Confirm & Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ── SYNC MODAL ──────────────────────────────────────────── */}
      {syncOpen && syncLot && (() => {
        const rows   = entries.filter(e => e.lot_no === syncLot && !+e.synced);
        const tOut   = rows.reduce((s,r) => s + +r.output_kg, 0);
        const bridge = rows.every(r => r.brand);
        return (
          <div className="sk-modal-overlay" onClick={e => e.target === e.currentTarget && setSyncOpen(false)}>
            <div className="sk-modal sk-modal-sm">
              <div className="sk-modal-header">
                <span className="sk-modal-title">🔄 Sync to Cold Store</span>
                <button className="sk-modal-close" onClick={() => setSyncOpen(false)}>✕</button>
              </div>
              <div className="sk-modal-body" style={{padding:'16px 20px'}}>
                <div className="sk-sync-info">
                  <div className="sk-sync-lbl">Ready to sync to Cold Store</div>
                  <div className="sk-sync-row"><strong>Lot:</strong> <span className="mono">{syncLot}</span></div>
                  <div className="sk-sync-row"><strong>Tubs:</strong> {rows.length} · <strong>Output:</strong> {tOut.toFixed(2)} KG ({(tOut*2.20462).toFixed(2)} lbs)</div>
                  <div className="sk-sync-row"><strong>Route:</strong> {rows[0]?.is_cooking ? '❄ IQF → V2&3' : rows[0]?.packing_line} → Cold Store</div>
                </div>
                <div className={`sk-bridge-check ${bridge ? 'bc-ok' : 'bc-warn'}`}>
                  <strong>{bridge ? '✓ All bridge fields present' : '⚠ Some bridge fields empty'}</strong>
                  <div style={{fontSize:'11px',marginTop:'3px',color:'#6b7280'}}>
                    {bridge ? 'Brand present — CS will auto-populate Product ID, Pack Style and NAV ID.'
                           : 'Brand is missing — will need manual entry at Cold Store.'}
                  </div>
                </div>
              </div>
              <div className="sk-modal-footer">
                <button className="sk-btn sk-btn-export" onClick={() => setSyncOpen(false)}>Cancel</button>
                <button className="sk-btn sk-btn-sync"   onClick={confirmSync}>✓ Confirm Sync</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* TOAST */}
      {toast && <div className={`sk-toast sk-toast-${toast.type}`}>{toast.msg}</div>}
    </div>
  );
};

export default SoakingDashboard;
